import { create } from 'zustand';

/**
 * Launcher V2 PR C QA follow-up U9 (item 18, R12) — where a rep's in-progress
 * sequence-step copy lives while they type.
 *
 * ── THE BUG ───────────────────────────────────────────────────────────────
 * `StepEditorShell` held its subject/body in `useDraftState`
 * (`web/app-home/src/hooks/useDraftState.ts`), which is component-local
 * `useState` under the hood: it survives same-key RE-RENDERS (its whole
 * purpose — SSE churn must not reseed a live draft) but nothing about an
 * UNMOUNT. `SequencesStep` renders the editor as
 * `<SequenceEditorShell key={selectedRung.draftKey} …>`, so clicking a
 * different rung on the rail unmounts the shell and every step editor inside
 * it. Typed copy was destroyed with no warning, and clicking back showed the
 * saved head as if nothing had been typed. Lifting the draft out of React —
 * the same move `rung-scope-store.ts` documents for the Audience Rule draft,
 * for the same structural reason — is the only fix that survives an unmount.
 *
 * ── SHAPE ─────────────────────────────────────────────────────────────────
 * One entry per edited step, keyed {@link copyDraftKey}
 * (`${scope}|${sequenceId}|${position}`) — the identical entity key
 * `StepEditorShell` already passed to `useDraftState`, plus an editing-scope
 * segment (D1 shared-edit-leak fix), so the addressing is otherwise
 * unchanged; only its lifetime and its scoping are.
 *
 * ── D1 SHARED-EDIT LEAK (2026-09-01) ────────────────────────────────────────
 * The key used to be bare `${sequenceId}:${position}`. Under the pre-D1 model
 * that was correct — every save advanced the shared head, so two rungs
 * mapping the same sequence really were editing the same copy. D1 added
 * branch-on-edit (a save on a shared sequence branches PER RUNG instead of
 * moving head), but the draft layer — which holds typing BEFORE any save —
 * was never rescoped to match: two rungs mapped to the same sequence read and
 * wrote the identical draft entry, so typing on one rung rendered live on the
 * other, before either had saved anything. `copyDraftKey` now takes a `scope`
 * — the editing surface's own identity (a rung's `draftKey` in the play flow,
 * or the literal `'library'` on the Config page) — so a draft belongs to
 * (scope, sequence, step) instead of just (sequence, step). Two rungs mapped
 * to the same sequence now get independent entries; the SAME rung switching
 * away and back still hits the same key (survival unchanged).
 *
 * FIXED (Codex adversarial-review round, 2026-09-02): a rung staged this
 * session has a `new:<n>` scope (`playbook-draft.ts`'s `nextNewDraftKey`). The
 * original D1 fix left what saving it did to that draft as an accepted edge
 * ("orphans, invisible until the next Play switch clears the store") — but
 * `nextNewDraftKey` REUSES a `new:<n>` key once the rung holding it is saved
 * and drops out of the draft's numbering, so the orphan does not just sit
 * there: the next unrelated rung a rep adds can be handed the identical key,
 * and if it maps the same sequence the first rung did, it silently inherits
 * the first rung's stale draft. That is the same cross-rung leak this whole
 * store exists to prevent, reached through key reuse instead of missing
 * scope, so it is closed rather than left as an edge:
 * `playbook-draft-store.ts`'s `adoptSavedPlaybookDraft` calls
 * {@link migrateCopyDraftScope} for every rung that just received a
 * server-minted id, re-keying its drafts from `new:<n>` to `rung:<id>` BEFORE
 * that `new:<n>` key can be reused.
 *
 * AN ENTRY EXISTS IF AND ONLY IF THE STEP IS DIRTY. That invariant is the
 * design, not an accident of usage, and two behaviors fall straight out of it:
 *
 *  - A CLEAN step has no entry, so it renders the CURRENT head. This is
 *    deliberately better than what `useDraftState` could do: that hook seeds
 *    per key and then ignores the seed forever, so a server head that moved
 *    under an untouched editor stayed invisible until remount. Here the head
 *    is read at render time and the draft only overrides it while dirty.
 *  - "Is this dirty?" needs no pristine copy, no shallow-equal at render, and
 *    no second map: `entries[key] !== undefined`. {@link editCopyDraft} keeps
 *    the invariant true by DELETING an entry that a rep types back to the
 *    head's own values, so a manual revert clears the indicator exactly like
 *    Discard does.
 *
 * The seed-once contract itself lives in {@link editCopyDraft}: the first
 * keystroke on a key seeds from the head passed at that moment, and every
 * later keystroke merges over the LIVE entry — a head that moves mid-typing
 * (an SSE-driven refetch, a sibling save) can never clobber it. There is no
 * mount-time `ensure` seed on purpose; seeding at mount would re-pin the stale
 * head on a clean key, which is precisely the regression the bullet above
 * avoids.
 *
 * Module-scope zustand (root CLAUDE.md "Zustand for Module-Scope Client
 * State"), modelled on `playbook-draft-store.ts` / `rung-scope-store.ts`:
 * plain `create<State>(…)`, exported {@link initialState} so tests reset with
 * `useSequenceCopyDraftStore.setState(initialState, true)`, and actions as
 * module-level functions rather than fields on the state object — that `true`
 * REPLACES the state, so actions held inside it would be wiped by the very
 * call tests use to reset.
 *
 * `PlayFlowShell` clears this store on its `[playId]` effect (top of setup AND
 * cleanup, StrictMode-resilient per that effect's own documented idiom), so
 * one Play's typed copy can never surface under another Play.
 *
 * ── WHICH WRITERS MUST BUMP `resetGenerations` (round-6 class audit) ───────
 * `RichHtmlField` is a seed-once DOM island: it only re-touches
 * `innerHTML` when its seedKey changes, and `SequenceStepEditor` builds that
 * seedKey as `[draftKey, renderedVersionId, resetGeneration, clearEpoch]`.
 * So any write into `entries` that originates OUTSIDE the editor — bypassing
 * the `onChange` that would otherwise have already put the value in the DOM —
 * has to make one of those ingredients move, or the contentEditable keeps
 * rendering stale HTML that the rep's next keystroke then commits over the
 * store's newer value (`onInput` commits the island's ENTIRE `innerHTML`).
 * A keystroke must NEVER bump: that is the caret-reset bug the seed-once
 * scheme exists to prevent.
 *
 * The line, per writer — derived once (2026-09-15, after a third consecutive
 * adversarial-review finding in this class) so the next reader does not have
 * to re-derive it:
 *
 *  - {@link editCopyDraft} — KEYSTROKE. Must not bump, and does not. The one
 *    keystroke path; every other writer here is out-of-editor.
 *  - {@link acceptGeneratedCopyDraft} — out-of-editor (AI Accept). Bumps
 *    per key. See its own doc for the original statement of this class.
 *  - {@link clearCopyDraft} — out-of-editor (Discard button). Bumps per key.
 *  - {@link clearSequenceCopyDrafts} — out-of-editor (Play switch). Bumps
 *    {@link selectClearEpoch} globally, because a per-key bump cannot fix a
 *    cross-Play seedKey collision (see that field's doc).
 *  - {@link clearCopyDraftIfUnchanged} — out-of-editor (post-save). Needs no
 *    bump, on two independent grounds: a save advances the head so
 *    `renderedVersionId` moves anyway, AND it only drops an entry that still
 *    equals what was dispatched (i.e. the new head), so the DOM is already
 *    showing exactly that HTML.
 *  - {@link clearCopyDrafts} — out-of-editor (mapping swap). Needs no bump:
 *    the rung is moving to a DIFFERENT sequence, so `draftKey`'s sequenceId
 *    segment moves, and the keys it clears are no longer addressed by any
 *    mounted editor.
 *  - {@link clearCopyDraftsForSequence} — out-of-editor (Gong flow rebind).
 *    Needs no bump: a rebind rewrites the head and its caller patches the
 *    head cache with the new versionId, so `renderedVersionId` moves.
 *  - {@link clearCopyDraftsInScope} — out-of-editor (Config Discard). Needs
 *    no bump: the caller navigates away immediately (see its own doc), and
 *    it deletes the generations for the keys it clears.
 *  - {@link migrateCopyDraftScope} — out-of-editor (rung-save re-key). Needs
 *    no bump: it changes the SCOPE segment, so `draftKey` itself moves.
 *    Pinned by a test in
 *    `web/app-home/src/__tests__/playbooks-copy-draft-survival.test.tsx`.
 *
 * A sender fork deliberately writes NOTHING here — see
 * `SequencesStep.tsx#handleSenderSaveNew` and
 * `docs/playbooks-usage-sidebar-spec.md`. The handoff that used to live there
 * is exactly what produced three rounds of findings in this class; it was
 * deleted rather than fixed a fourth time. Any NEW out-of-editor writer added
 * to this store has to answer this table before it ships.
 */

/** A step's editable copy — the same two fields `SequenceStep` exposes to the editor. */
export interface CopyDraftValues {
  subject: string;
  body: string;
}

interface SequenceCopyDraftState {
  /** Dirty steps only, keyed by {@link copyDraftKey}. A missing key means "clean — follow the head". */
  entries: Record<string, CopyDraftValues>;
  /**
   * Per-key counter bumped by {@link clearCopyDraft} (Discard) AND by
   * {@link acceptGeneratedCopyDraft} (Accept generated copy, Codex
   * adversarial-review bug 1, 2026-09-02) — Batch 3 (F2)'s rich-body
   * `bodyFormat` addition. `RichHtmlField`/`RichTextBodyField` are seed-once
   * DOM islands: they only re-touch the contentEditable DOM when their
   * `seedKey` changes, and neither `draftKey` nor `renderedVersionId` (the
   * other two seedKey ingredients, see `SequenceStepEditor.tsx`) changes on a
   * Discard OR an Accept — the entry just disappears (Discard) or is
   * overwritten (Accept) in `entries`. Without this counter, either action on
   * a rich body would leave stale HTML showing in the DOM even though `value`
   * had already moved on. A missing key defaults to 0 (never bumped).
   */
  resetGenerations: Record<string, number>;
  /**
   * Module-scope counter bumped by {@link clearSequenceCopyDrafts} —
   * `PlayFlowShell`'s `[playId]` reset (Codex adversarial-review bug 2,
   * 2026-09-02), the fourth `SequenceStepEditor.tsx` rich-body seedKey
   * ingredient. That reset runs in a passive `useEffect`, i.e. AFTER the new
   * Play's tree has already committed its first render — so if the new
   * Play's `[draftKey, renderedVersionId, resetGeneration]` tuple happens to
   * collide with the previous Play's (plausible: this reset resets
   * `resetGenerations` to `{}`, and a lookup against an already-empty map for
   * a never-set key also reads as generation 0), the rich DOM island would
   * never re-seed and would silently keep showing the PREVIOUS Play's stale
   * HTML. Bumping a value with NO per-key scoping — unlike `resetGenerations`
   * — guarantees the seedKey tuple changes across every clear, regardless of
   * that collision.
   */
  clearEpoch: number;
}

/** Exported per the store convention — reset idiom is `useSequenceCopyDraftStore.setState(initialState, true)`. */
export const initialState: SequenceCopyDraftState = {
  entries: {},
  resetGenerations: {},
  clearEpoch: 0,
};

export const useSequenceCopyDraftStore = create<SequenceCopyDraftState>(() => ({ ...initialState }));

/**
 * The entity key for one step's copy: `${scope}|${sequenceId}|${position}`.
 *
 * `scope` is the editing SURFACE, not the sequence — a rung's `draftKey`
 * (`playbook-draft.ts`'s `rungDraftKey()`/`new:N`) in the play flow, or the
 * string literal `'library'` on the Config page's `SequenceCopyPane`. This is
 * the D1 shared-edit-leak fix (see module doc): without it, two rungs mapped
 * to the same sequence shared one entry.
 *
 * `|` is the separator, not `:` — rung scope values already contain `:`
 * (`rung:<id>`, `new:<n>`), so reusing `:` here would make prefix matching
 * (see {@link sequenceHasCopyDrafts}) ambiguous between a scope boundary and
 * one inside the scope string.
 *
 * Position, not step id — a `SequenceStep` has no id (a version is a
 * whole-snapshot array, Key Decision 12), and position is what every other
 * per-step lookup in this tree already addresses by.
 */
export function copyDraftKey(scope: string, sequenceId: string, position: number): string {
  return `${scope}|${sequenceId}|${position}`;
}

function sameValues(a: CopyDraftValues, b: CopyDraftValues): boolean {
  return a.subject === b.subject && a.body === b.body;
}

/** Returns `entries` without `key`, or the SAME object when the key isn't there (no needless re-render). */
function withoutKey(
  entries: Record<string, CopyDraftValues>,
  key: string,
): Record<string, CopyDraftValues> {
  if (entries[key] === undefined) return entries;
  const { [key]: _dropped, ...rest } = entries;
  return rest;
}

/**
 * Applies one field edit to a step's draft, seeding from `head` on the first
 * keystroke for that key.
 *
 * `head` is a parameter rather than store state because the head belongs to a
 * react-query cache entry that this store must not mirror (mirroring it is how
 * a stale copy of the server's state gets pinned in the first place). It is
 * read ONLY when there is no live entry yet — see the module doc's seed-once
 * note.
 *
 * Drops the entry when the merged value equals the head, keeping the
 * "entry exists iff dirty" invariant true for a rep who types an edit and then
 * types it back out.
 */
export function editCopyDraft(
  key: string,
  head: CopyDraftValues,
  patch: Partial<CopyDraftValues>,
): void {
  useSequenceCopyDraftStore.setState((state) => {
    const next = { ...(state.entries[key] ?? head), ...patch };
    if (sameValues(next, head)) {
      if (state.entries[key] === undefined) return state;
      const { [key]: _dropped, ...rest } = state.entries;
      return { entries: rest };
    }
    return { entries: { ...state.entries, [key]: next } };
  });
}

/**
 * Writes an externally-generated (AI) value into a step's draft — the Accept
 * control's write path (`handleAcceptGenerated`, `SequencesStep.tsx`). Same
 * seed-from-head / merge / drop-if-equal-to-head behavior as
 * {@link editCopyDraft} (the entry-iff-dirty invariant holds here too), PLUS
 * a bump of {@link selectResetGeneration}'s counter for this key.
 *
 * Codex adversarial-review bug 1 (2026-09-02): `handleAcceptGenerated` used
 * to call `editCopyDraft` directly — the SAME entry point a keystroke uses —
 * which seemed right (the module doc's "indistinguishable from hand-typed
 * copy" design) but misses one thing a keystroke's caller always satisfies
 * for free: a keystroke already committed through `RichHtmlField`'s own
 * `onChange`, so the DOM is already showing what it wrote. An Accept writes
 * straight into this store from OUTSIDE the editor, bypassing that DOM
 * commit entirely, so none of the OTHER seedKey ingredients (`draftKey`,
 * `renderedVersionId`) move — the rich body's contentEditable DOM island
 * would keep showing the OLD head HTML while the store (and therefore what
 * Save persists) already holds the generated copy. This is a SEPARATE entry
 * point from `editCopyDraft`, deliberately: a keystroke must NEVER bump this
 * counter (that would reintroduce the caret-reset bug the seed-once scheme
 * exists to prevent), so `editCopyDraft` itself is untouched and remains the
 * only keystroke path.
 */
export function acceptGeneratedCopyDraft(
  key: string,
  head: CopyDraftValues,
  patch: Partial<CopyDraftValues>,
): void {
  useSequenceCopyDraftStore.setState((state) => {
    const resetGenerations = {
      ...state.resetGenerations,
      [key]: (state.resetGenerations[key] ?? 0) + 1,
    };
    const next = { ...(state.entries[key] ?? head), ...patch };
    if (sameValues(next, head)) {
      if (state.entries[key] === undefined) return { resetGenerations };
      const { [key]: _dropped, ...rest } = state.entries;
      return { entries: rest, resetGenerations };
    }
    return { entries: { ...state.entries, [key]: next }, resetGenerations };
  });
}

/**
 * Drops one step's draft unconditionally — the Discard control. There is
 * nothing to write and nothing to confirm: the store holds only the diff
 * against the saved head, so dropping the entry IS "revert to the saved copy".
 *
 * The save path deliberately uses {@link clearCopyDraftIfUnchanged} instead.
 *
 * Also bumps {@link selectResetGeneration}'s counter for this key (see the
 * state field's doc) — Discard is the one drop-path that must force a
 * rich-body DOM island to re-seed, since the drop itself changes neither of
 * the seedKey's other two ingredients.
 */
export function clearCopyDraft(key: string): void {
  useSequenceCopyDraftStore.setState((state) => ({
    entries: withoutKey(state.entries, key),
    resetGenerations: { ...state.resetGenerations, [key]: (state.resetGenerations[key] ?? 0) + 1 },
  }));
}

/**
 * Drops a draft ONLY if it still holds exactly the values that were dispatched
 * to the server — the save path's version of `useDraftState`'s
 * `reseedIfUnchanged`, and for the identical reason: a rep who keeps typing
 * between `saveSequenceSteps(...)` and its response would otherwise have those
 * newer keystrokes thrown away by a save that never carried them. Keeping the
 * entry also keeps the "Unsaved changes" marker up, which is then accurate —
 * what is on screen genuinely is not what was persisted.
 */
export function clearCopyDraftIfUnchanged(key: string, dispatched: CopyDraftValues): void {
  useSequenceCopyDraftStore.setState((state) => {
    const entry = state.entries[key];
    if (entry === undefined || !sameValues(entry, dispatched)) return state;
    const { [key]: _dropped, ...rest } = state.entries;
    return { entries: rest };
  });
}

/**
 * Drops every draft AND every reset-generation counter — `PlayFlowShell`'s
 * `[playId]` reset (see the module doc). Every consuming component fully
 * unmounts/remounts across a Play switch anyway, so the generations are pure
 * hygiene here (nothing reads a stale key's counter across this reset), but
 * clearing them keeps this store from accumulating one entry per Discard for
 * the lifetime of the browser session.
 *
 * Also bumps {@link selectClearEpoch}'s counter (Codex adversarial-review bug
 * 2, 2026-09-02, see that field's doc) — unconditionally, even when there was
 * nothing to clear, since the whole point is to guarantee this call always
 * changes the rich-body seedKey tuple regardless of what else did or didn't
 * collide.
 */
export function clearSequenceCopyDrafts(): void {
  useSequenceCopyDraftStore.setState((state) => ({
    entries: {},
    resetGenerations: {},
    clearEpoch: state.clearEpoch + 1,
  }));
}

/**
 * Moves every entry scoped under `oldScope` to the equivalent key under
 * `newScope` — Codex adversarial-review fix (2026-09-02), closing the leak the
 * module doc's "KNOWN EDGE, ACCEPTED" paragraph used to describe.
 *
 * The edge that was accepted turned out to be worse than "an orphaned,
 * invisible entry": `nextNewDraftKey` (`playbook-draft.ts`) computes the next
 * `new:<n>` key as one past the highest `new:` key CURRENTLY IN THE DRAFT, so
 * once a `new:1` rung is saved and drops out of the draft's `new:` numbering,
 * the NEXT rung a rep adds also becomes `new:1` — the SAME key, reused. If the
 * new rung maps the same sequence the first one did, it inherits the first
 * rung's stale draft under the identical key, which is the exact cross-rung
 * leak D1 fixed, just reached through key reuse instead of missing scope.
 *
 * `playbook-draft-store.ts`'s `adoptSavedPlaybookDraft` is the one caller: for
 * every rung that just transitioned from its `new:<n>` staging key to its
 * server-minted `rung:<id>` key, it re-keys that rung's drafts here BEFORE the
 * `new:<n>` key can be handed to a different rung. A no-op (same object
 * reference back) when `oldScope` holds nothing, so a rung that was never
 * typed on costs nothing.
 */
export function migrateCopyDraftScope(oldScope: string, newScope: string): void {
  useSequenceCopyDraftStore.setState((state) => {
    const oldPrefix = `${oldScope}|`;
    let changed = false;
    const entries: Record<string, CopyDraftValues> = {};
    for (const [key, value] of Object.entries(state.entries)) {
      if (key.startsWith(oldPrefix)) {
        entries[`${newScope}|${key.slice(oldPrefix.length)}`] = value;
        changed = true;
      } else {
        entries[key] = value;
      }
    }
    return changed ? { entries } : state;
  });
}

/**
 * Drops every entry for one (scope, sequenceId) — Codex adversarial-review fix
 * (2026-09-02): `SequenceMappingDrawer`'s "Replace sequence" confirm dialog
 * tells the rep that swapping a rung's mapping "drops that association and
 * any unsaved copy drafts for the current mapping — this can't be undone",
 * but until this fix the handler only staged the new mapping and left the old
 * sequence's drafts sitting in this store, dormant, keyed by
 * `${scope}|${oldSequenceId}|position`. Re-mapping the SAME rung back to the
 * SAME old sequence would then resurrect them — contradicting the dialog's
 * explicit "can't be undone" promise. This is what makes that promise true:
 * called on confirm, for the rung's own scope and the sequence it is leaving.
 */
export function clearCopyDrafts(scope: string, sequenceId: string): void {
  useSequenceCopyDraftStore.setState((state) => {
    const prefix = `${scope}|${sequenceId}|`;
    const keys = Object.keys(state.entries).filter((key) => key.startsWith(prefix));
    if (keys.length === 0) return state;
    const entries = { ...state.entries };
    for (const key of keys) delete entries[key];
    return { entries };
  });
}

/**
 * Drops every entry for `sequenceId`, ACROSS every scope/rung — Live-QA
 * round 2 Batch 6 fix (Fix 3). {@link clearCopyDrafts} clears only ONE scope
 * (the calling rung's own `draftKey`), which is correct for `confirmSwap` (a
 * per-rung mapping-change operation — only the initiating rung's association
 * changes, so only its own drafts need to go) but wrong for a Gong flow
 * REBIND (`SequenceMappingDrawer.tsx`'s `openRebindConfirm`): a rebind
 * rewrites the sequence's SHARED head, which every OTHER rung/playbook
 * mapping this same sequence also renders. Clearing only the initiating
 * rung's scope leaves any OTHER rung's unsaved drafts against this sequence
 * stale-keyed to the OLD head, so they overlay on the NEW head the next time
 * that other rung's editor renders — contradicting the rebind confirm
 * dialog's own claim that "any unsaved copy drafts anyone has typed" are
 * replaced.
 *
 * Keys are `${scope}|${sequenceId}|${position}` ({@link copyDraftKey}); this
 * matches on the MIDDLE segment regardless of what `scope` is, so it reaches
 * every rung's drafts against `sequenceId` in one pass. Scope values never
 * contain `|` (they use `:` for their own internal separators — `rung:<id>`,
 * `new:<n>`, `library`), so splitting on `|` unambiguously isolates the
 * sequence-id segment.
 *
 * `confirmSwap`'s call site is UNCHANGED by this fix — it keeps using
 * {@link clearCopyDrafts}, per this batch's explicit scope fence (a mapping
 * swap is a different, per-rung operation, not an identity-level rewrite).
 */
export function clearCopyDraftsForSequence(sequenceId: string): void {
  useSequenceCopyDraftStore.setState((state) => {
    const keys = Object.keys(state.entries).filter((key) => {
      const parts = key.split('|');
      return parts.length === 3 && parts[1] === sequenceId;
    });
    if (keys.length === 0) return state;
    const entries = { ...state.entries };
    for (const key of keys) delete entries[key];
    return { entries };
  });
}

/**
 * Plan 2026-09-28-006 U3a (rulebook R3/R7; replaces plan 2026-09-27-007 U4's
 * unscoped `rekeyCopyDraftsForSequence`) — moves the entries of ONE editing
 * scope (a rung's `rungDraftKey`) for sequence `fromId` onto the equivalent
 * key under `toId`, carrying each key's reset-generation counter with it.
 * Called by `ensure-play-owned.ts#adoptPlayLadderMoves` when an in-play fork moved
 * that one group from `fromId` onto the play's copy `toId`: the group's step
 * editors now address `scope|toId|…`, so its typing must follow. Every OTHER
 * scope stays put — a fork is made for one group, and a second group still on
 * `fromId` keeps its own unsaved typing under `fromId`.
 *
 * Matches the scope AND sequence segments exactly (`parts[0] === scope &&
 * parts[1] === fromId`), the same way {@link clearCopyDraftsForSequence}
 * parses keys. No epoch bump: the moved entry's KEY is itself a seedKey
 * ingredient, so the editor under the new key seeds from the moved entry. A
 * no-op (same state object) when nothing matches or `fromId === toId`.
 */
export function rekeyCopyDraftsForRung(scope: string, fromId: string, toId: string): void {
  if (fromId === toId) return;
  useSequenceCopyDraftStore.setState((state) => {
    const moveKey = (key: string): string | null => {
      const parts = key.split('|');
      return parts.length === 3 && parts[0] === scope && parts[1] === fromId ? `${scope}|${toId}|${parts[2]}` : null;
    };
    let changed = false;
    const entries: Record<string, CopyDraftValues> = {};
    for (const [key, value] of Object.entries(state.entries)) {
      const moved = moveKey(key);
      if (moved !== null) changed = true;
      entries[moved ?? key] = value;
    }
    const resetGenerations: Record<string, number> = {};
    for (const [key, value] of Object.entries(state.resetGenerations)) {
      const moved = moveKey(key);
      if (moved !== null) changed = true;
      resetGenerations[moved ?? key] = value;
    }
    return changed ? { entries, resetGenerations } : state;
  });
}

/**
 * Codex review of Playbooks QA round 5 (finding B1) — the scoped twin of
 * {@link clearSequenceCopyDrafts}, for a "Discard changes" action that must
 * only ever destroy ITS OWN editing surface's drafts, never every scope's.
 *
 * Before this fix, `PlaybooksConfigPage`'s Discard button called the
 * unscoped, whole-store `clearSequenceCopyDrafts` even after
 * `useHasUnsavedWork('library')` (R5-3) started DETECTING dirt scoped to
 * `'library'` only — so a genuinely surviving Play-flow `rung:*`/`new:*`
 * draft (kept alive on purpose by the Back-button design, see
 * `UnsavedWorkGuard.tsx`'s module doc) got silently destroyed the moment a
 * rep discarded an unrelated Config edit.
 *
 * Prefix-matches on `${scope}|`, the same convention {@link
 * migrateCopyDraftScope} and every scoped selector in `unsaved-guard.ts`
 * use. Does NOT bump {@link selectClearEpoch}'s global counter — unlike the
 * full-store clear, a scoped discard's caller (Config) unmounts immediately
 * after via its own navigation, so there is no other mounted
 * `SequenceStepEditor` in this scope left to force-reseed.
 */
export function clearCopyDraftsInScope(scope: string): void {
  useSequenceCopyDraftStore.setState((state) => {
    const prefix = `${scope}|`;
    const keys = Object.keys(state.entries).filter((key) => key.startsWith(prefix));
    if (keys.length === 0) return state;
    const entries = { ...state.entries };
    const resetGenerations = { ...state.resetGenerations };
    for (const key of keys) {
      delete entries[key];
      delete resetGenerations[key];
    }
    return { entries, resetGenerations };
  });
}

/**
 * Selector factory for one step's draft entry.
 *
 * Returns the stored object by reference (or `undefined`), so a subscribed
 * `StepEditorShell` re-renders only when ITS key changes — not on every
 * keystroke a sibling step receives.
 */
export function selectCopyDraftEntry(
  key: string,
): (state: SequenceCopyDraftState) => CopyDraftValues | undefined {
  return (state) => state.entries[key];
}

/**
 * Playbooks QA round 4, U11 (E1) / Decision 14 — the entries for ONE editing
 * surface's one sequence: every key with the `${scope}|${sequenceId}|` prefix.
 *
 * ── WHY (the E1 typing lag) ───────────────────────────────────────────────
 * `SequencesStep` and `MembersPanel` both subscribed to the WHOLE `entries`
 * map (`useSequenceCopyDraftStore((s) => s.entries)`). `editCopyDraft` yields
 * a brand-new `entries` object on every keystroke, so a keystroke in ANY
 * rung's editor re-rendered both of those trees — including the roster gap
 * scan — even for a completely unrelated rung's copy.
 *
 * Scoping the subscription means only edits to THIS rung+sequence's own steps
 * can trigger those re-renders. Paired with `useShallow`
 * (`zustand/react/shallow`) at the call site, because this returns a FRESH
 * object each call: shallow key/value comparison is what makes an unrelated
 * edit a no-op rather than a new reference every time the store changes at
 * all.
 *
 * Prefix-matching on the key rather than a second index: `copyDraftKey`'s
 * format is `${scope}|${sequenceId}|${position}`, and `entries` holds only
 * DIRTY steps (see this module's doc) — so this scan is over the handful of
 * steps a rep has actually touched this session, not the whole library.
 */
export function selectCopyDraftEntriesFor(
  scope: string,
  sequenceId: string,
): (state: SequenceCopyDraftState) => Record<string, CopyDraftValues> {
  const prefix = `${scope}|${sequenceId}|`;
  return (state) => {
    const scoped: Record<string, CopyDraftValues> = {};
    for (const [key, values] of Object.entries(state.entries)) {
      if (key.startsWith(prefix)) scoped[key] = values;
    }
    return scoped;
  };
}

/**
 * Selector factory for one step's reset-generation counter (see the state
 * field's doc on {@link clearCopyDraft}) — the third ingredient of
 * `SequenceStepEditor.tsx`'s rich-body `seedKey`. Returns `0` for a key that
 * has never been discarded, by reference-stable default (no object identity
 * churn on every render of a step that has never hit Discard).
 */
export function selectResetGeneration(key: string): (state: SequenceCopyDraftState) => number {
  return (state) => state.resetGenerations[key] ?? 0;
}

/**
 * Selector for the module-scope clear-epoch counter (see the state field's
 * doc on {@link clearSequenceCopyDrafts}) — the fourth ingredient of
 * `SequenceStepEditor.tsx`'s rich-body `seedKey`, threaded in alongside
 * {@link selectResetGeneration}. Unlike that selector, this one takes no key:
 * the counter is module-scope, not per-entry, by design — the guarantee it
 * gives is "this changed across ANY clear", not "this changed for MY key".
 */
export function selectClearEpoch(state: SequenceCopyDraftState): number {
  return state.clearEpoch;
}

/** True when this step has unsaved typing — see the module doc's entry-iff-dirty invariant. */
export function isCopyDraftDirty(entry: CopyDraftValues | undefined): boolean {
  return entry !== undefined;
}

/** What the editor renders: the live draft while dirty, otherwise the current head. */
export function resolveCopyDraftValues(
  entry: CopyDraftValues | undefined,
  head: CopyDraftValues,
): CopyDraftValues {
  return entry ?? head;
}

/**
 * The minimum a step has to look like to be merged: a position to key by, and
 * the two copy fields. Structural rather than `SequenceStep` so this module
 * stays free of `schemas.ts` (and its zod), and generic at the call site so a
 * real `SequenceStep` comes back OUT as a `SequenceStep`, Gong descriptor and
 * all.
 */
interface MergeableStep {
  position: number;
  subject?: string | null;
  body?: string | null;
}

/**
 * Playbooks QA (prod round, 2026-09-16), Fix 3 — `savedSteps` with this
 * surface's unsaved drafts overlaid, keyed by position.
 *
 * CANONICAL "why" for Preview-shows-drafts; call sites point here. Preview
 * used to render the SAVED head, so a rep who edited a step and clicked
 * Preview saw the OLD copy under a gap banner counting the new one. Preview,
 * that gap check and `previewStepCopySegments` must all read the SAME steps,
 * hence one helper. It deliberately does NOT save on the way in — Preview is a
 * read-only view of the live draft, and the "Unsaved changes" badge stays up.
 *
 * ── THE CONTRACT ─────────────────────────────────────────────────────────────
 *  - A draft REPLACES both copy fields of its position. It is safe to take
 *    both from the entry (rather than patching one) because of the store's
 *    seed-once invariant: {@link editCopyDraft} seeds a new entry from `head`,
 *    so an entry always carries a complete `{subject, body}`, not a partial.
 *  - Every other field of a merged step survives (`gongStepDescriptor` in
 *    particular — it is what drives the preview's per-step rich/plain
 *    decision, so dropping it would silently reformat exactly the steps a rep
 *    had edited).
 *  - A position with no draft is passed through BY REFERENCE, and a steps
 *    array with no drafts at all comes back as the SAME array. That reference
 *    stability is load-bearing rather than tidy: the result feeds
 *    `useDeferredValue` and a `useMemo`'d roster scan, so a fresh array every
 *    render would re-run the whole gap check for a sequence nobody is editing.
 *  - A draft for a position the head does not have is IGNORED (a step deleted
 *    under an open editor): the preview shows the steps that exist rather than
 *    resurrecting one that does not.
 *
 * `scope`/`sequenceId` are taken rather than pre-narrowed keys because
 * {@link copyDraftKey} is the one place the key format lives; `drafts` may be
 * either the whole `entries` map or {@link selectCopyDraftEntriesFor}'s
 * narrowed one, since the key is rebuilt for lookup either way.
 */
export function mergeDraftsIntoSteps<T extends MergeableStep>(
  savedSteps: readonly T[],
  drafts: Record<string, CopyDraftValues>,
  scope: string,
  sequenceId: string,
): readonly T[] {
  let anyApplied = false;
  const merged = savedSteps.map((step) => {
    const draft = drafts[copyDraftKey(scope, sequenceId, step.position)];
    if (draft === undefined) return step;
    anyApplied = true;
    return { ...step, subject: draft.subject, body: draft.body };
  });
  return anyApplied ? merged : savedSteps;
}

/**
 * True when ANY step of `sequenceId` UNDER `scope` has unsaved typing — the
 * rail card's "Unsaved changes" marker, which is the whole point of the
 * indicator: it has to be visible on a rung the rep is NOT currently looking
 * at, since switching away is the moment the old build lost the copy.
 *
 * `scope` is now required (D1 shared-edit-leak fix — see module doc): the
 * badge is per EDITING SURFACE, not per sequence, because two rungs mapped to
 * the same sequence are no longer "editing the same copy" under branch-on-edit
 * — a save on one branches only that rung.
 *
 * Prefix-matches on `${scope}|${sequenceId}|` INCLUDING both separators, so
 * `seq-1` never claims `seq-10`'s drafts and `rung:a` never claims `rung:ab`'s.
 */
export function sequenceHasCopyDrafts(
  state: SequenceCopyDraftState,
  scope: string,
  sequenceId: string | null | undefined,
): boolean {
  if (!sequenceId) return false;
  const prefix = `${scope}|${sequenceId}|`;
  return Object.keys(state.entries).some((key) => key.startsWith(prefix));
}

/**
 * True when ANY step of `sequenceId` has unsaved typing UNDER ANY SCOPE —
 * round-2-of-Batch-6-hardening fix (Codex finding 3): the Gong flow rebind
 * confirm dialog's draft-loss warning (`SequenceMappingDrawer.tsx`'s
 * `openRebindConfirm`) needs to reflect the TRUE clearing scope of a rebind's
 * success handler, which calls {@link clearCopyDraftsForSequence} — every
 * rung/scope mapping this sequence, not just the initiating rung. Checking
 * only the initiating rung's own scope (via {@link sequenceHasCopyDrafts})
 * would silently miss a DIFFERENT rung's unsaved drafts against the same
 * shared sequence, showing no warning right before that other rung's real
 * work is destroyed.
 *
 * Matches on the MIDDLE segment the same way {@link clearCopyDraftsForSequence}
 * does, regardless of `scope` — see that function's doc for why splitting on
 * `|` unambiguously isolates the sequence-id segment.
 *
 * `confirmSwap`'s draft check (a per-rung-only operation, not identity-level)
 * keeps using the rung-scoped {@link sequenceHasCopyDrafts} — this helper is
 * only for identity-level clears.
 */
export function sequenceHasAnyCopyDrafts(
  state: SequenceCopyDraftState,
  sequenceId: string | null | undefined,
): boolean {
  if (!sequenceId) return false;
  return Object.keys(state.entries).some((key) => {
    const parts = key.split('|');
    return parts.length === 3 && parts[1] === sequenceId;
  });
}
